import { rendererLogger } from './logger';
export const SAMPLE_WINDOW = 12;
export const EVAL_EVERY = 6;
export const WARMUP_MS = 500;
export const SEEK_THRESHOLD_MS = 1000;
export const SEEK_COOLDOWN_MS = 10000;
export const SEEK_END_GUARD_MS = 3000;
export const DEADBAND_ENTER_MS = 10;
export const DEADBAND_EXIT_MS = 20;
// 1000 ms of error asks for a full 100 % rate change; clamped to the max delta below.
export const RATE_GAIN_PER_MS = 1 / 1000;
export const MAX_RATE_DELTA_SILENT = 0.1;
export const MAX_RATE_DELTA_AUDIBLE = 0.02;
export const RATE_MIN_STEP = 0.002;
export const SETTLE_NOTICE_S = 20;
export const MAX_SEEK_LEAD_MS = 2000;
const ITEM_END_RETENTION_MS = 60000;
const systemClock = { wallNow: () => Date.now(), perfNow: () => performance.now() };
/**
 * Closed-loop video-wall sync: measures the presented media position against the
 * schedule via requestVideoFrameCallback, nudges playbackRate for small errors and
 * seeks only for large ones.
 */
export class VideoSyncController {
    constructor(clock = systemClock) {
        this.clock = clock;
        this.cur = null;
        this.gen = 0;
        this.unsupportedLogged = false;
        // Stop commands arrive before the play action attaches, so ends are remembered per item.
        this.itemEnds = [];
        // A late-join sync command can arrive before the play action attaches its item.
        this.pendingSyncItemId = null;
        // How far ahead of target a seek must aim to land on time: a device property, kept across items.
        this.seekLeadMs = 0;
    }
    attach(el, target) {
        this.detach();
        const syncExpected = this.pendingSyncItemId === target.itemId;
        this.pendingSyncItemId = null;
        if (typeof el.requestVideoFrameCallback !== 'function') {
            if (!this.unsupportedLogged) {
                this.unsupportedLogged = true;
                rendererLogger.notice('[vsync] requestVideoFrameCallback unavailable; video sync correction disabled');
            }
            return;
        }
        const gen = ++this.gen;
        const a = {
            ...target,
            el: el,
            gen,
            callback: (now, meta) => this.onFrame(gen, now, meta),
            handle: null,
            samples: [],
            sinceEval: 0,
            warmupUntil: null,
            firstFrameAt: null,
            lastSeekAt: null,
            seekLanding: false,
            seeksFutile: false,
            inDeadband: false,
            suspended: syncExpected,
            seeks: 0,
            startErrMs: null,
            lastErrMs: null,
            settledAfterS: null,
        };
        this.cur = a;
        a.handle = a.el.requestVideoFrameCallback(a.callback);
    }
    setItemEnd(itemId, endMs) {
        const cutoff = this.clock.wallNow() - ITEM_END_RETENTION_MS;
        this.itemEnds = this.itemEnds.filter(e => e.endMs >= cutoff && !(e.itemId === itemId && e.endMs === endMs));
        this.itemEnds.push({ itemId, endMs });
    }
    /** Called as the legacy late-join sync arrives, so the controller cannot race its pause/seek. */
    expectSync(itemId) {
        if (this.cur?.itemId === itemId) {
            this.pendingSyncItemId = null;
            this.suspend(itemId);
        }
        else {
            this.pendingSyncItemId = itemId;
        }
    }
    suspend(itemId) {
        const a = this.cur;
        if (!a || a.itemId !== itemId)
            return;
        a.suspended = true;
        a.seekLanding = false;
        a.el.playbackRate = 1;
        this.rearm(a);
    }
    resume(itemId) {
        if (this.pendingSyncItemId === itemId)
            this.pendingSyncItemId = null;
        const a = this.cur;
        if (!a || a.itemId !== itemId || !a.suspended)
            return;
        a.suspended = false;
        this.rearm(a);
    }
    detach() {
        const a = this.cur;
        if (!a)
            return;
        this.cur = null;
        if (a.handle !== null && typeof a.el.cancelVideoFrameCallback === 'function') {
            a.el.cancelVideoFrameCallback(a.handle);
        }
        a.el.playbackRate = 1;
        if (a.el.preservesPitch === false)
            a.el.preservesPitch = true;
        this.logSummary(a);
    }
    /** detach() plus forgetting a pending sync that never attached (idle). */
    reset() {
        this.detach();
        this.pendingSyncItemId = null;
    }
    getStatus() {
        const a = this.cur;
        if (!a || a.lastErrMs === null)
            return null;
        return { offsetMs: a.lastErrMs, rate: a.el.playbackRate };
    }
    onFrame(gen, now, meta) {
        const a = this.cur;
        if (!a || a.gen !== gen)
            return;
        a.handle = a.el.requestVideoFrameCallback(a.callback);
        if (a.suspended || a.el.seeking)
            return;
        if (a.firstFrameAt === null)
            a.firstFrameAt = now;
        if (a.warmupUntil === null)
            a.warmupUntil = now + WARMUP_MS;
        if (now < a.warmupUntil)
            return;
        const displayWall = this.clock.wallNow() - this.clock.perfNow() + (meta.expectedDisplayTime ?? now);
        const period = loopPeriodS(a.el);
        let errMs = (meta.mediaTime - this.targetS(a, displayWall, period)) * 1000;
        if (period !== null)
            errMs = wrapHalf(errMs, period * 1000);
        a.samples.push(errMs);
        if (a.samples.length > SAMPLE_WINDOW)
            a.samples.shift();
        if (++a.sinceEval >= EVAL_EVERY) {
            a.sinceEval = 0;
            this.evaluate(a, now);
        }
    }
    evaluate(a, now) {
        const e = median(a.samples);
        const abs = Math.abs(e);
        a.lastErrMs = e;
        if (a.startErrMs === null)
            a.startErrMs = e;
        if (a.seekLanding) {
            a.seekLanding = false;
            if (this.seekLeadMs >= MAX_SEEK_LEAD_MS && e < -SEEK_THRESHOLD_MS / 2) {
                a.seeksFutile = true;
                rendererLogger.notice(`[vsync] seeks off item=${a.itemId} landed=${Math.round(e)}ms at max lead`);
            }
            this.seekLeadMs = clamp(this.seekLeadMs - e, 0, MAX_SEEK_LEAD_MS);
        }
        if (abs > SEEK_THRESHOLD_MS && this.canSeek(a, now)) {
            const period = loopPeriodS(a.el);
            a.el.playbackRate = 1;
            a.el.currentTime = this.targetS(a, this.clock.wallNow() + this.seekLeadMs, period);
            a.lastSeekAt = now;
            a.seeks++;
            a.seekLanding = true;
            this.rearm(a);
            rendererLogger.notice(`[vsync] seek item=${a.itemId} err=${Math.round(e)}ms lead=${Math.round(this.seekLeadMs)}ms`);
            return;
        }
        if (a.inDeadband ? abs <= DEADBAND_EXIT_MS : abs < DEADBAND_ENTER_MS) {
            if (!a.inDeadband && a.settledAfterS === null && a.firstFrameAt !== null) {
                a.settledAfterS = (now - a.firstFrameAt) / 1000;
            }
            a.inDeadband = true;
            if (a.el.playbackRate !== 1)
                a.el.playbackRate = 1;
            return;
        }
        a.inDeadband = false;
        const audible = isAudible(a.el);
        const maxDelta = audible ? MAX_RATE_DELTA_AUDIBLE : MAX_RATE_DELTA_SILENT;
        const rate = 1 - clamp(e * RATE_GAIN_PER_MS, -maxDelta, maxDelta);
        if (Math.abs(rate - a.el.playbackRate) > RATE_MIN_STEP) {
            // Time-stretching is wasted CPU when nothing is heard.
            if (typeof a.el.preservesPitch === 'boolean' && a.el.preservesPitch !== audible)
                a.el.preservesPitch = audible;
            a.el.playbackRate = rate;
        }
    }
    canSeek(a, now) {
        if (a.suspended || a.seeksFutile)
            return false;
        if (a.lastSeekAt !== null && now - a.lastSeekAt < SEEK_COOLDOWN_MS)
            return false;
        const end = this.itemEndFor(a);
        return end === null || end - this.clock.wallNow() > SEEK_END_GUARD_MS;
    }
    itemEndFor(a) {
        let end = null;
        for (const e of this.itemEnds) {
            if (e.itemId === a.itemId && e.endMs > a.anchorMs && (end === null || e.endMs < end))
                end = e.endMs;
        }
        return end;
    }
    targetS(a, wallMs, period) {
        const t = (a.startAtMs + wallMs - a.anchorMs) / 1000;
        return period === null ? t : ((t % period) + period) % period;
    }
    rearm(a) {
        a.samples = [];
        a.sinceEval = 0;
        a.warmupUntil = null;
        a.inDeadband = false;
    }
    logSummary(a) {
        if (a.startErrMs === null || a.lastErrMs === null)
            return;
        const ranS = a.firstFrameAt === null ? 0 : (this.clock.perfNow() - a.firstFrameAt) / 1000;
        const unsettled = a.settledAfterS === null ? ranS >= SETTLE_NOTICE_S : a.settledAfterS > SETTLE_NOTICE_S;
        const settled = a.settledAfterS === null ? 'no' : `${a.settledAfterS.toFixed(1)}s`;
        const line = `[vsync] item=${a.itemId} start=${Math.round(a.startErrMs)}ms settled=${settled} seeks=${a.seeks} residual=${Math.round(a.lastErrMs)}ms`;
        if (a.seeks > 0 || unsettled || Math.abs(a.startErrMs) > SEEK_THRESHOLD_MS) {
            rendererLogger.notice(line);
        }
        else {
            rendererLogger.info(line);
        }
    }
}
function loopPeriodS(el) {
    const d = el.duration;
    return el.loop && Number.isFinite(d) && d > 0 ? d : null;
}
/** Wraps into (-period/2, period/2] so a loop seam does not read as a ±period error. */
function wrapHalf(x, period) {
    return x - period * Math.ceil(x / period - 0.5);
}
function median(xs) {
    const s = [...xs].sort((a, b) => a - b);
    const mid = s.length >> 1;
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}
function clamp(x, lo, hi) {
    return Math.min(hi, Math.max(lo, x));
}
/** Unknown (no detection API) counts as audible — the conservative cap. */
function isAudible(el) {
    if (el.muted)
        return false;
    const bytes = el.webkitAudioDecodedByteCount;
    const moz = el.mozHasAudio;
    const tracks = el.audioTracks;
    if (typeof bytes !== 'number' && typeof moz !== 'boolean' && !tracks)
        return true;
    return (bytes ?? 0) > 0 || moz === true || (tracks?.length ?? 0) > 0;
}
//# sourceMappingURL=videoSync.js.map